import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FieldArray, type FormikProps } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import { AutoSizer, List } from 'react-virtualized'

import Dropdown from 'ui/Dropdown'
import { isActingAsFounder, isActingAsFundManager } from 'selectors/auth'
import { getIsFounderVerified } from 'selectors/company'
import BoxGroupsAndUsersChips from 'components/BoxGroupsAndUsersChips'
import ListsSelectionBox from 'components/ListsSelectionBox'
import FileBanner from 'components/FileBanner'
import ReadOnlyTooltip from 'components/ReadOnlyTooltip/ReadOnlyTooltip'
import { useAppSelector } from 'utils/hooks/reduxToolkit'
import useGroupCompany from 'utils/hooks/useGroupCompany'
import { Metric } from 'utils/types/metricsV2'
import { Group } from 'utils/types/user'
import {
  AutoSuggestPortfolioCompanies,
  ListsSelectionBoxWrapper,
} from '../CreateMetric/CreateMetric.styles'
import {
  ImportMetricValuesContainer,
  TableWrapper,
  DataPointsContainer,
  TableHeader,
  TableHeaderItem,
  OptionalLabel,
  TableContent,
  ShareGroupsContainer,
  MetricValuesContainer,
} from './BulkImportMetricValuesModal.styles'
import ImportMetricValueRow from './ImportMetricValueRow'

interface GroupItem {
  id: string
  listId: string
  listName: string
}

interface Props {
  formik: FormikProps<{
    metricValues: []
  }>
  handleAddGroup: (group: Group) => void
  handleAddGroupByHandle: (groupHandle: string) => void
  handleRemoveGroup: (id: string) => void
  loadGroups: (search: string) => void
  currentGroups: Group[]
  countImported: number
  fileName?: string
  handleDeleteItemList: (itemId: string) => void
  handleEditItemList: (item: GroupItem) => void
  handleAddItemList: (item: GroupItem) => void
  itemLists: Group[]
  metric: Metric
}

const ImportMetricValues = ({
  formik,
  handleAddGroup,
  handleAddGroupByHandle,
  handleRemoveGroup,
  loadGroups,
  currentGroups,
  countImported,
  fileName,
  handleDeleteItemList,
  handleEditItemList,
  handleAddItemList,
  itemLists,
  metric,
}: Props) => {
  const intl = useIntl()
  const isFounder = useAppSelector(isActingAsFounder)
  const isFounderVerified = useAppSelector(getIsFounderVerified)

  const isFundManager = useAppSelector(isActingAsFundManager)
  const groupCompany = useGroupCompany()
  const isFundCompanyMetric = metric.subject.id === groupCompany?.id

  const showShareWithOption =
    isFounder || (isFundManager && isFundCompanyMetric)

  const rowRenderer =
    (remove) =>
    ({ index, key, style }) => {
      return (
        <div key={key} style={style}>
          <ImportMetricValueRow
            name={`metricValues.${index}`}
            index={index}
            remove={remove}
            values={formik.values.metricValues[index]}
            errors={formik.errors?.metricValues?.[index]}
            touched={formik.touched?.metricValues?.[index]}
            showDelete={formik.values.metricValues.length > 1}
            handleChange={formik.handleChange}
            handleBlur={formik.handleBlur}
            setFieldValue={formik.setFieldValue}
          />
        </div>
      )
    }

  return (
    <MetricValuesContainer>
      <FileBanner fileName={fileName}>
        <FormattedMessage
          id="bulkImportDataPointsModal.countImported"
          values={{ count: countImported }}
        />
      </FileBanner>

      <ImportMetricValuesContainer>
        <DataPointsContainer withCustomWidth={showShareWithOption}>
          <TableWrapper>
            <TableHeader>
              <TableHeaderItem flex={0.1} />
              <TableHeaderItem flex={1}>
                <FormattedMessage id="bulkImportDataPointsModal.metricValue" />
              </TableHeaderItem>
              <TableHeaderItem flex={0.6} minWidth="16rem">
                <FormattedMessage id="bulkImportDataPointsModal.metricDate" />
              </TableHeaderItem>
              <TableHeaderItem flex={0.1} />
            </TableHeader>

            <TableContent withoutScroll={formik.values.metricValues.length < 7}>
              <FieldArray name="metricValues">
                {({ remove }) => (
                  <AutoSizer disableHeight>
                    {({ width }) => (
                      <List
                        height={Math.min(
                          showShareWithOption ? 450 : 320,
                          50 * formik.values.metricValues.length
                        )}
                        overscanRowCount={10}
                        rowCount={formik.values.metricValues.length}
                        rowHeight={50}
                        rowRenderer={rowRenderer(remove)}
                        width={width}
                        style={{ marginTop: '1rem' }}
                      />
                    )}
                  </AutoSizer>
                )}
              </FieldArray>
            </TableContent>
          </TableWrapper>
        </DataPointsContainer>
        {showShareWithOption && (
          <ReadOnlyTooltip
            place="left"
            text={intl.formatMessage({ id: 'updates.readOnlyMetrics' })}
            disabled={!isFounderVerified}
            topOffset={-10}
            leftOffset={-50}
          >
            {({ disabled }) => (
              <ShareGroupsContainer>
                <TableHeaderItem disabled={disabled}>
                  <FontAwesomeIcon icon={['far', 'share']} />{' '}
                  <FormattedMessage id="metrics.shareWith" />{' '}
                  <OptionalLabel>(optional)</OptionalLabel>
                </TableHeaderItem>

                <ListsSelectionBoxWrapper>
                  <ListsSelectionBox
                    showLabel={false}
                    items={itemLists}
                    handleAddGroup={handleAddItemList}
                    handleAddUser={() => {}}
                    handleDelete={handleDeleteItemList}
                    handleEditItemList={handleEditItemList}
                    onlyGroups
                  />
                </ListsSelectionBoxWrapper>

                <AutoSuggestPortfolioCompanies>
                  <Dropdown
                    name="companiesOrPortfolios"
                    onSelectOption={handleAddGroup}
                    placeholder={intl.formatMessage({
                      id: 'metrics.addGroups',
                    })}
                    id="companies-portfolios"
                    loadOptions={loadGroups}
                    async
                    onPressEnter={handleAddGroupByHandle}
                    type="input"
                    icon={['far', 'plus']}
                    iconFontSize="1.8rem"
                    getOption={(entity) => ({
                      label: entity.name,
                      id: entity.id,
                    })}
                    clearOnSelect
                    disabled={disabled}
                  />

                  <BoxGroupsAndUsersChips
                    items={currentGroups?.map((item) => ({
                      ...item,
                      fromMetric: true,
                    }))}
                    handleDelete={handleRemoveGroup}
                  />
                </AutoSuggestPortfolioCompanies>
              </ShareGroupsContainer>
            )}
          </ReadOnlyTooltip>
        )}
      </ImportMetricValuesContainer>
    </MetricValuesContainer>
  )
}

export default ImportMetricValues
